import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, MailCheck, KeyRound } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message || "Reset link sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 grain">
      <div className="w-full max-w-md relative z-10 fade-up">
        <Link to="/login" data-testid="back-login-link" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
        <div className="border border-white/10 bg-[#121212] p-8 sm:p-10">
          {sent ? (
            <div data-testid="forgot-sent" className="text-center space-y-4">
              <MailCheck size={40} className="text-[#FF3B30] mx-auto" />
              <h1 className="font-heading text-3xl font-black uppercase tracking-tighter">Check Your Email</h1>
              <p className="text-sm text-zinc-400">If an account exists for <span className="text-white">{email}</span>, we've sent a link to reset the password. The link expires in 1 hour.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-1">
                <KeyRound size={18} className="text-[#FF3B30]" />
                <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">Reset Access</span>
              </div>
              <h1 className="font-heading text-4xl font-black uppercase tracking-tighter mb-2">Forgot Password</h1>
              <p className="text-sm text-zinc-400 mb-8">Enter your admin email and we'll send a reset link.</p>
              <form onSubmit={submit} className="space-y-5">
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Email</Label>
                  <Input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#FF3B30]" placeholder="you@troytested.com" />
                </div>
                <button data-testid="forgot-submit-btn" type="submit" disabled={busy}
                  className="w-full bg-white text-black font-bold py-3.5 uppercase tracking-wider text-sm hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-50">
                  {busy ? "Sending..." : "Send Reset Link"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
